import "./ResetPassword.css";
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { Toast } from "../../helpers/alert";
import { generateRegex } from "../../helpers/Regex";
import {
  checkResetToken,
  confirmResetPassword,
} from "../../redux/apiCalls/forgot_passCall/forgot_passCall";
import { getPassPolicies } from "../../redux/apiCalls/usersCall.js/passPoliciesCall";
import ExpiredIllustration from "../../assets/Time_management-rafiki.svg";
import AutenticationIllustration from "../../assets/Authentication-rafiki.svg";

const ResetPassword = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const token = new URLSearchParams(search).get("token");
  const { passPolicies } = useSelector((s) => s.passPolicies);
  const reset = useSelector((s) => s.forgotPassword) || {};
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});

  const { checkReason } = reset;


  const tokenInvalid = useMemo(
    () => !token || reset?.checkError === true,
    [token, reset?.checkError]
  );

  useEffect(() => {
    if (token) checkResetToken(dispatch, token);
    if (!passPolicies) getPassPolicies(dispatch);
  }, [dispatch, token]);

  const validate = (input) => {
    const e = {};
    const pr = passPolicies
      ? generateRegex(passPolicies)
      : { regex: /.{1,}/, messageRegex: "" };

  if (!pr.regex.test(input.newPassword)) {
      e.newPassword =
        pr.messageRegex ||
        intl.formatMessage({
          id: "Password.GenericPolicy",
          defaultMessage: "La contraseña no cumple con las políticas.",
        });
    }
 if (input.newPassword !== input.confirmPassword) {
      e.confirmPassword = intl.formatMessage({
        id: "errors.ConfirmPass",
        defaultMessage: "Las contraseñas deben coincidir",
      });
    }
    return e;
  };

  const onChange = (e) => {
    const next = { ...form, [e.target.name]: e.target.value };
    setForm(next);
    setErrors(validate(next));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const currentErrors = validate(form);
    setErrors(currentErrors);
    if (Object.keys(currentErrors).length > 0) return;

    if (!token) {
      Toast.fire({
        icon: "error",
        title: intl.formatMessage({
          id: "Token.Missing",
          defaultMessage: "Falta el enlace o es inválido.",
        }),
      });
      return;
    }

    const ok = await confirmResetPassword(dispatch, {
      token,
      pass: form.newPassword,
    });

    if (ok) {
      Toast.fire({
        icon: "success",
        title: intl.formatMessage({
          id: "Password.Success",
          defaultMessage: "Tu contraseña se cambió correctamente.",
        }),
      });
      navigate("/login");
    }
  };


  const expiredTitleText =
    checkReason === "used"
      ? intl.formatMessage({
          id: "Password.TokenAlreadyUsed",
          defaultMessage: "La clave ya fue reseteada.",
        })
      : checkReason === "expired"
      ? intl.formatMessage({
          id: "Password.TokenExpired",
          defaultMessage: "El enlace expiró.",
        })
      : intl.formatMessage({
          id: "Password.TokenInvalidOrExpired",
          defaultMessage: "El enlace de recuperación es inválido o expiró.",
        });


  const invalidImgSrc =
    checkReason === "used" ? AutenticationIllustration : ExpiredIllustration;

  const invalidImgAlt =
    checkReason === "used"
      ? intl.formatMessage({
          id: "Password.TokenUsedImageAlt",
          defaultMessage: "Token ya utilizado",
        })
      : intl.formatMessage({
          id: "Password.TokenExpiredImageAlt",
          defaultMessage: "Token expirado",
        });

  if (tokenInvalid) {
    return (
      <div
        className="modal d-block resetpassword"
        tabIndex="-1"
        aria-labelledby="expiredTitle"
        aria-modal="true"
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="expiredTitle">
                {expiredTitleText}
              </h1>
            </div>
            <div
              className="modal-body modal-pass"
              style={{ textAlign: "center" }}
            >
              <img
                src={invalidImgSrc}
                alt={invalidImgAlt}
                style={{ maxWidth: "100%", height: "auto", marginBottom: 16 }}
              />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-closeModal"
                onClick={() => navigate("/login")}
              >
                <FormattedMessage
                  id="ProfileInformation.Close"
                  defaultMessage="Cerrar"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="modal d-block resetpassword"
      tabIndex="-1"
      aria-labelledby="resetPasswordTitle"
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="resetPasswordTitle">
              <FormattedMessage
                id="ForgotPassword.SetNew"
                defaultMessage="Configura una nueva contraseña"
              />
            </h1>
          </div>
          <div className="modal-body modal-pass">
            <div className="cont-pass">
              <label className="label-pass">
                <FormattedMessage
                  id="ProfileInformation.NewPass"
                  defaultMessage="Nueva contraseña"
                />
                :{" "}
              </label>
              <input
                className="inpt-pass"
                type="password"
                name="newPassword"
                value={form.newPassword}
                onChange={onChange}
                autoComplete="new-password"
                required
              />
              {errors.newPassword && (
                <span className="error-pass">{errors.newPassword}</span>
              )}
            </div>
            <div className="cont-pass">
              <label className="label-pass">
                <FormattedMessage
                  id="ProfileInformation.ConfirmPass"
                  defaultMessage="Confirmar nueva contraseña"
                />
                :{" "}
              </label>
              <input
                className="inpt-pass"
                type="password"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={onChange}
                autoComplete="new-password"
                required
              />
              {errors.confirmPassword && (
                <span className="error-pass">{errors.confirmPassword}</span>
              )}
            </div>

            <p className="hint" style={{ marginTop: 8 }}>
              <small>
                <FormattedMessage
                  id="ForgotPassword.TokenHint"
                  defaultMessage="Si ya expiró, solicita un enlace nuevo."
                />
              </small>
            </p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-closeModal"
              onClick={() => navigate("/login")}
            >
              <FormattedMessage
                id="ProfileInformation.Close"
                defaultMessage="Cerrar"
              />
            </button>
            <button
              type="button"
              className="btn btn-change"
              onClick={onSubmit}
              disabled={reset.isSubmitting}
            >
              {reset.isSubmitting
                ? intl.formatMessage({
                    id: "Saving",
                    defaultMessage: "Guardando...",
                  })
                : intl.formatMessage({ id: "Save", defaultMessage: "Guardar" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
